import type * as hast from 'hast';

import {
    BLOCK_DRAWING,
    BLOCK_IMAGE,
    type BlockImageData,
    type BlockImageSizeValue,
    FRAGMENT_CAPTION,
    INLINE_IMAGE,
    type InlineImageData,
    type InlineImageSize,
    paragraph,
} from '@gitbook/doc-core';
import { Block, Fragment, Inline } from '@gitbook/slate';

import type { HastToGastTransformMap } from './types';
import { getEnumAttribute } from './utils';

export const transformImages: HastToGastTransformMap = {
    img: async (node: hast.Element, _eat, context, options) => {
        const width = parseImageSize(node.properties?.width);
        const height = parseImageSize(node.properties?.height);

        // the `alt` property is either used as a caption for the image or as an alt text
        const altAttribute =
            typeof node.properties?.alt === 'string' ? node.properties.alt.trim() : '';

        const captionNodes = context.parsedCaption
            ? context.parsedCaption
            : [paragraph.createParagraph(altAttribute)];
        const blockAltData = context.parsedCaption ? { alt: altAttribute } : {};

        const ref =
            typeof node.properties?.src === 'string'
                ? await options.resolveFile(node.properties.src, {
                      fileName: context.parsedCaption ? '' : altAttribute,
                  })
                : undefined;

        if (isDrawingImage(node) && (!ref || ref.kind === 'file')) {
            return [
                Block.create({
                    type: BLOCK_DRAWING,
                    data: {
                        ...blockAltData,
                        ref,
                    },
                    nodes: [],
                    isVoid: true,
                    fragments: [
                        Fragment.create({
                            fragment: FRAGMENT_CAPTION,
                            nodes: captionNodes,
                        }),
                    ],
                }),
            ];
        }

        if (!ref) {
            return [];
        }

        const refDark = context.darkModePictureSource
            ? await options.resolveFile(context.darkModePictureSource)
            : undefined;

        if (context.parseImagesAsBlocks) {
            const imageBlockData: BlockImageData = {
                ...blockAltData,
                ...(refDark ? { refDark } : {}),
                ...(height ? { height } : {}),
                ...(width ? { width } : {}),
                ref,
            };

            return [
                Block.create({
                    type: BLOCK_IMAGE,
                    isVoid: true,
                    data: imageBlockData,
                    fragments: [
                        Fragment.create({
                            fragment: FRAGMENT_CAPTION,
                            nodes: captionNodes,
                        }),
                    ],
                }),
            ];
        }
        const size = getEnumAttribute<InlineImageSize>(node.properties, 'dataSize', [
            'original',
            'line',
        ]);

        const imageInlineData: InlineImageData = {
            ref,
            alt: altAttribute,
            ...(refDark ? { refDark } : {}),
            ...(size ? { size } : {}),
        };

        return [
            Inline.create({
                type: INLINE_IMAGE,
                isVoid: true,
                data: imageInlineData,
            }),
            // As inline images do not have a Fragment for captions,we format the caption block separately to avoid loss of data
            ...(context.parsedCaption ? context.parsedCaption : []),
        ];
    },
};

/**
 * Checks if a given HAST element is a drawing image.
 * A drawing image is an image element with the 'gitbook-drawing' class.
 */
function isDrawingImage(node: hast.Element): boolean {
    return Boolean(
        node.properties?.className &&
            Array.isArray(node.properties.className) &&
            node.properties.className.includes('gitbook-drawing')
    );
}

/**
 * Parses an image size value that can be either a number or a string which consists of two parts
 * a number and unit (e.g. '100px'). Note that the only valid unit for this value is '%'.
 */
function parseImageSize(size: string | number | unknown): BlockImageSizeValue | undefined {
    if (typeof size !== 'string' && typeof size !== 'number') {
        return undefined;
    }

    let value: number;
    let unit: string | undefined;

    if (typeof size === 'number') {
        value = size;
    } else {
        // Use a regular expression to extract the unit from the input size string. This is done to
        // avoid the use of the "replace" method with an untrusted input, which could potentially
        // introduce a security vulnerability by allowing the input to contain malicious code.
        // By using a regular expression to extract the unit, we can ensure that only valid units
        // are extracted and not any arbitrary string.
        const unitMatch = /^(\d+(?:\.\d+)?)(%)$/.exec(size);
        if (unitMatch) {
            value = Number.parseFloat(unitMatch[1]);
            unit = unitMatch[2];
        } else {
            // If the value doesn't have an accepted sign at the end, it's considered to be a numerical value.
            // In case the value is incorrect, such as "100pxts"," it won't be accepted.
            const numberMatch = /\d+/.exec(size);
            if (!numberMatch) {
                return undefined;
            }
            value = Number.parseFloat(numberMatch[0]);
        }
    }

    return unit === undefined ? value : { value, unit };
}
